// AdminDashboard.js
import React, { useState, useEffect } from 'react';
import apiService, { errorHelpers, formatHelpers } from '../../utils/apiService';
import AdminOverview from './admin/AdminOverview';
import UserManagement from './admin/UserManagement';
import CertificateUpload from './admin/CertificateUpload';
import CertificateManagement from './admin/CertificateManagement';
import VerificationReports from './admin/VerificationReports';
import SystemSettings from './admin/SystemSettings';
import ActivityLogs from './admin/ActivityLogs';
import UploadReports from './admin/UploadReports';

const AdminDashboard = ({ activeTab, setActiveTab, user }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        try {
            const response = await apiService.admin.getDashboardAnalytics();
            setAnalytics(response.data.analytics);
        } catch (error) {
            console.error('Failed to load analytics:', error);
            setAlert({
                type: 'error',
                message: 'Failed to load dashboard analytics'
            });
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'users':
                return <UserManagement />;
            case 'upload':
                return <CertificateUpload onUploadComplete={loadAnalytics} />;
            case 'certificates':
                return <CertificateManagement />;
            case 'reports':
                return <VerificationReports />;
            case 'upload-reports':
                return <UploadReports />;
            case 'settings':
                return <SystemSettings />;
            case 'logs':
                return <ActivityLogs />;
            case 'overview':
            default:
                return <AdminOverview analytics={analytics} loading={loading} />;
        }
    };

    return (
        <div className="admin-dashboard">
            {alert && (
                <div className={`alert alert-${alert.type} mb-4`}>
                    {alert.message}
                    <button
                        onClick={() => setAlert(null)}
                        className="ml-4 text-lg font-bold cursor-pointer bg-transparent border-none"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="dashboard-header">
                <div>
                    <h1 className="dashboard-title">
                        Admin Dashboard
                    </h1>
                    <p className="text-gray-600">
                        Manage users, certificates, and system settings.
                    </p>
                </div>

                <div className="user-info flex items-center gap-4">
                    <div className="text-right">
                        <div className="font-medium">{user.firstname} {user.lastname}</div>
                        <div className="text-sm text-gray-500 capitalize">{user.role.replace('_', ' ')}</div>
                    </div>
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.firstname.charAt(0)}{user.lastname.charAt(0)}
                    </div>
                </div>
            </div>

            {/* Quick Stats Cards - Only show on overview */}
            {activeTab === 'overview' && analytics && (
                <div className="stats-grid mb-6">
                    <StatCard
                        title="Total Users"
                        value={analytics.total_users}
                        icon="👥"
                        color="blue"
                        change={`+${analytics.recent_users} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Total Certificates"
                        value={analytics.total_certificates}
                        icon="📜"
                        color="green"
                    />
                    <StatCard
                        title="Total Verifications"
                        value={analytics.total_authentications}
                        icon="✅"
                        color="purple"
                        change={`+${analytics.recent_authentications} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Total Revenue"
                        value={formatHelpers.formatCurrency(analytics.total_revenue)}
                        icon="💰"
                        color="yellow"
                        change={`+${formatHelpers.formatCurrency(analytics.recent_revenue)} this month`}
                        changeType="positive"
                    />
                    <StatCard
                        title="Success Rate"
                        value={`${analytics.success_rate}%`}
                        icon="📊"
                        color="green"
                    />
                </div>
            )}

            {/* Quick Action Cards - Only show on overview */}
            {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <QuickActionCard
                        icon="👥"
                        title="Manage Users"
                        description="View and manage user accounts"
                        onClick={() => setActiveTab('users')}
                        color="blue"
                    />
                    <QuickActionCard
                        icon="📤"
                        title="Upload Certificates"
                        description="Bulk upload certificates via CSV"
                        onClick={() => setActiveTab('upload')}
                        color="green"
                    />
                    <QuickActionCard
                        icon="📊"
                        title="View Reports"
                        description="Check verification reports"
                        onClick={() => setActiveTab('reports')}
                        color="purple"
                    />
                    <QuickActionCard
                        icon="⚙️"
                        title="System Settings"
                        description="Configure system parameters"
                        onClick={() => setActiveTab('settings')}
                        color="yellow"
                    />
                </div>
            )}

            {/* Tab Content */}
            <div className="tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

// Stat Card Component
const StatCard = ({ title, value, icon, color, change, changeType }) => {
    const colorClasses = {
        blue: 'border-l-blue-500 bg-blue-50',
        green: 'border-l-green-500 bg-green-50',
        purple: 'border-l-purple-500 bg-purple-50',
        yellow: 'border-l-yellow-500 bg-yellow-50'
    };

    const changeColorClasses = {
        positive: 'text-green-600',
        negative: 'text-red-600',
        neutral: 'text-gray-600'
    };

    return (
        <div className={`stat-card ${colorClasses[color]}`}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-2xl">{icon}</div>
                <div className="text-right">
                    <div className="stat-number">{value}</div>
                </div>
            </div>
            <div className="stat-label">{title}</div>
            {change && (
                <div className={`stat-change ${changeColorClasses[changeType]}`}>
                    {change}
                </div>
            )}
        </div>
    );
};

// Quick Action Card Component
const QuickActionCard = ({ icon, title, description, onClick, color = 'blue' }) => {
    const colorClasses = {
        blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50',
        green: 'border-green-200 hover:border-green-400 hover:bg-green-50',
        purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50',
        yellow: 'border-yellow-200 hover:border-yellow-400 hover:bg-yellow-50'
    };

    const iconColorClasses = {
        blue: 'text-blue-600',
        green: 'text-green-600',
        purple: 'text-purple-600',
        yellow: 'text-yellow-600'
    };

    return (
        <div
            className={`card cursor-pointer transition-all duration-300 ${colorClasses[color]}`}
            onClick={onClick}
        >
            <div className="card-body text-center">
                <div className={`text-2xl mb-2 ${iconColorClasses[color]}`}>
                    {icon}
                </div>
                <h3 className="text-sm font-semibold mb-1">{title}</h3>
                <p className="text-gray-600 text-xs">{description}</p>
            </div>
        </div>
    );
};

export default AdminDashboard;